import * as React from "react";
import { View, Text, Pressable } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { IconButton } from "./icon-button";

type CalloutSize = "xs" | "s" | "m" | "l" | "xl";
type CalloutTone = "neutral" | "brand" | "pink";

type CalloutProps = {
  icon?: React.ComponentProps<typeof Ionicons>["name"];
  tone?: CalloutTone;
  size?: CalloutSize;
  title: string;
  description?: string;
  action?: { label: string; onPress: () => void };
  onDismiss?: () => void;
  className?: string;
};

const iconContainerClass: Record<CalloutTone, string> = {
  neutral: "bg-accent/40",
  brand: "bg-brand/15",
  pink: "bg-pink/15",
};

type SizeConfig = {
  container: string;
  gap: string;
  iconWrapper: string;
  iconGlyph: number;
  title: string;
  description: string;
  cta: string;
  ctaText: string;
  dismiss: React.ComponentProps<typeof IconButton>["size"];
};

const sizeConfig: Record<CalloutSize, SizeConfig> = {
  xs: {
    container: "gap-2 p-2.5",
    gap: "gap-1",
    iconWrapper: "h-7 w-7",
    iconGlyph: 14,
    title: "text-xs",
    description: "text-[11px]",
    cta: "h-7 px-2.5",
    ctaText: "text-xs",
    dismiss: "sm",
  },
  s: {
    container: "gap-2.5 p-3",
    gap: "gap-1",
    iconWrapper: "h-8 w-8",
    iconGlyph: 16,
    title: "text-sm",
    description: "text-xs",
    cta: "h-8 px-3",
    ctaText: "text-xs",
    dismiss: "sm",
  },
  m: {
    container: "gap-3 p-4",
    gap: "gap-1.5",
    iconWrapper: "h-10 w-10",
    iconGlyph: 20,
    title: "text-base",
    description: "text-sm",
    cta: "h-9 px-3.5",
    ctaText: "text-sm",
    dismiss: "sm",
  },
  l: {
    container: "gap-3.5 p-5",
    gap: "gap-2",
    iconWrapper: "h-12 w-12",
    iconGlyph: 24,
    title: "text-lg",
    description: "text-sm",
    cta: "h-10 px-4",
    ctaText: "text-sm",
    dismiss: "md",
  },
  xl: {
    container: "gap-4 p-6",
    gap: "gap-2",
    iconWrapper: "h-14 w-14",
    iconGlyph: 28,
    title: "text-xl",
    description: "text-base",
    cta: "h-12 px-5",
    ctaText: "text-base",
    dismiss: "md",
  },
};

/**
 * Inline promotional card used to surface a feature the user hasn't discovered.
 * Purely presentational — analytics and visibility rules live in
 * `features/callouts`.
 */
export function Callout({
  icon,
  tone = "brand",
  size = "m",
  title,
  description,
  action,
  onDismiss,
  className,
}: CalloutProps) {
  const [foreground, mutedForeground] = useCSSVariable([
    "--color-foreground",
    "--color-muted-foreground",
  ]) as [string, string];
  const config = sizeConfig[size];

  return (
    <View
      className={cn(
        "bg-card border-border w-full flex-row items-start rounded-lg border",
        config.container,
        className
      )}
    >
      {icon && (
        <View
          className={cn(
            "items-center justify-center rounded-full",
            config.iconWrapper,
            iconContainerClass[tone]
          )}
        >
          <Ionicons name={icon} size={config.iconGlyph} color={foreground} />
        </View>
      )}

      <View className={cn("flex-1", config.gap)}>
        <Text
          className={cn(
            "font-display text-foreground font-semibold",
            config.title
          )}
        >
          {title}
        </Text>
        {description && (
          <Text
            className={cn("text-muted-foreground", config.description)}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {description}
          </Text>
        )}
        {action && (
          <Pressable
            onPress={action.onPress}
            accessibilityRole="button"
            className={cn(
              "active:bg-accent mt-1 flex-row items-center justify-center self-start rounded-md border border-white bg-transparent",
              config.cta
            )}
          >
            <Text
              className={cn("text-foreground font-semibold", config.ctaText)}
            >
              {action.label}
            </Text>
          </Pressable>
        )}
      </View>

      {onDismiss && (
        <IconButton
          size={config.dismiss}
          surface="transparent"
          accessibilityLabel="Dispensar"
          onPress={onDismiss}
        >
          <Ionicons name="close" size={18} color={mutedForeground} />
        </IconButton>
      )}
    </View>
  );
}
